import { create } from 'zustand';
import {
  Node, Edge, OnNodesChange, OnEdgesChange,
  applyNodeChanges, applyEdgeChanges, XYPosition, Connection,
} from '@xyflow/react';
import {
  Scenario, SwimlaneDiagram, StepNodeData, LaneNodeData, SwimlaneConnection,
  CollisionError, SCENARIO_COLORS, LANE_COLORS, Partner, DEFAULT_PARTNERS,
} from '../types';
import { detectCollisions } from '../utils/collisionDetector';
import sc000UserJourney from '../data/sc000UserJourney';

/* ─── Constants ─── */
const STEP_WIDTH = 160;
const STEP_HEIGHT = 44;
const STEP_GAP_X = 50;
const STEP_GAP_Y = 30;
const LANE_LABEL_WIDTH = 140;
const LANE_PADDING_Y = 16;
const LANE_PADDING_X = 20;
const SCENARIO_GAP_X = 60;
const POSITIONS_KEY = 'diagrams-node-positions-v1';
const WAYPOINTS_KEY = 'diagrams-edge-waypoints-v1';
const PARTNERS_KEY = 'diagrams-partners-v1';
const LANE_HEIGHTS_KEY = 'diagrams-lane-heights-v1';
const TOTAL_WIDTH_KEY = 'diagrams-total-width-v1';

/* ─── Lane size persistence ─── */
function loadLaneHeights(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(LANE_HEIGHTS_KEY) || '{}'); } catch { return {}; }
}
function saveLaneHeights(v: Record<string, number>) {
  try { localStorage.setItem(LANE_HEIGHTS_KEY, JSON.stringify(v)); } catch (_) { /* ignore */ }
}
function loadTotalWidth(): number | null {
  try { const v = localStorage.getItem(TOTAL_WIDTH_KEY); return v ? Number(v) : null; } catch { return null; }
}
function saveTotalWidth(v: number | null) {
  try {
    if (v === null) localStorage.removeItem(TOTAL_WIDTH_KEY);
    else localStorage.setItem(TOTAL_WIDTH_KEY, String(v));
  } catch (_) { /* ignore */ }
}

/* ─── Partner persistence (localStorage cache) ─── */
function loadPartners(): Partner[] {
  try {
    const raw = localStorage.getItem(PARTNERS_KEY);
    return raw ? JSON.parse(raw) : DEFAULT_PARTNERS;
  } catch { return DEFAULT_PARTNERS; }
}

function savePartners(partners: Partner[]) {
  try { localStorage.setItem(PARTNERS_KEY, JSON.stringify(partners)); } catch (_) { /* ignore */ }
}

/* ─── Position persistence (localStorage cache) ─── */
function loadPositions(): Record<string, XYPosition> {
  try {
    const raw = localStorage.getItem(POSITIONS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function savePositions(positions: Record<string, XYPosition>) {
  try {
    localStorage.setItem(POSITIONS_KEY, JSON.stringify(positions));
  } catch { /* ignore */ }
}

/* ─── Waypoint persistence (edge mid-points) ─── */
function loadWaypoints(): Record<string, XYPosition> {
  try {
    const raw = localStorage.getItem(WAYPOINTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveWaypoints(waypoints: Record<string, XYPosition>) {
  try {
    localStorage.setItem(WAYPOINTS_KEY, JSON.stringify(waypoints));
  } catch { /* ignore */ }
}

/* ─── Store interface ─── */
interface DiagramStore {
  scenarios: Scenario[];
  nodes: Node[];
  edges: Edge[];
  collisions: CollisionError[];
  selectedNodeId: string | null;
  selectedScenarioId: string | null;
  nodePositions: Record<string, XYPosition>;
  edgeWaypoints: Record<string, XYPosition>;
  partners: Partner[];
  manualLaneHeights: Record<string, number>;
  manualTotalWidth: number | null;
  loading: boolean;

  addScenario: (diagram: SwimlaneDiagram) => void;
  removeScenario: (id: string) => void;
  deleteScenario: (id: string) => Promise<void>;
  createScenario: (diagram: SwimlaneDiagram) => Promise<void>;
  toggleScenario: (id: string) => void;
  reorderScenarios: (fromIndex: number, toIndex: number) => void;

  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  updateNodePosition: (id: string, position: XYPosition) => void;
  updateEdgeWaypoint: (edgeId: string, pos: XYPosition) => void;
  clearEdgeWaypoint: (edgeId: string) => void;
  addPartner: (partner: Partner) => void;
  removePartner: (id: string) => void;
  addConnection: (connection: Connection, scenarioColor: string, scenarioId: string) => void;
  removeConnection: (edgeId: string) => void;
  reconnectConnection: (oldEdgeId: string, newConnection: Connection) => void;
  setLaneHeight: (laneId: string, height: number) => void;
  setTotalWidth: (width: number | null) => void;
  addStep: (scenarioId: string, step: import('../types').SwimlaneStep) => void;
  selectNode: (id: string | null) => void;

  rebuildDiagram: () => void;
  validate: () => CollisionError[];

  loadFromApi: () => Promise<void>;
  saveToApi: () => Promise<void>;
}

/* ─── Build React Flow nodes/edges from scenarios ─── */

/**
 * Layout strategy: horizontal swimlane
 * - Lanes = horizontal rows (one per zone)
 * - Scenarios laid out left-to-right; each scenario occupies a column group
 * - Steps follow topological order (BFS)
 * - Lanes dynamically expand horizontally to fit all content
 */
function buildNodes(
  scenarios: Scenario[],
  positions: Record<string, XYPosition>,
  partners: Partner[] = [],
  manualLaneHeights: Record<string, number> = {},
  manualTotalWidth: number | null = null,
): Node[] {
  const nodes: Node[] = [];

  if (scenarios.length === 0) return nodes;

  // 1. Collect unique lanes from ALL scenarios (visible + hidden)
  //    For partner-* lanes, resolve label/color from the partners config
  const laneMap = new Map<string, { label: string; color: string; order: number }>();
  for (const sc of scenarios) {
    for (const lane of sc.diagram.lanes) {
      if (!laneMap.has(lane.id)) {
        let label = lane.label;
        let color = lane.color || LANE_COLORS[lane.id] || '#f5f5f5';

        // Override label/color from partner config if available
        if (lane.id.startsWith('partner-')) {
          const partnerId = lane.id.replace('partner-', '');
          const partnerConfig = partners.find(p => p.id === partnerId);
          if (partnerConfig) {
            label = partnerConfig.name;
            color = partnerConfig.color;
          }
        }

        laneMap.set(lane.id, {
          label,
          color,
          order: lane.order ?? laneMap.size,
        });
      }
    }
  }

  const sortedLanes = Array.from(laneMap.entries())
    .sort((a, b) => a[1].order - b[1].order);

  // 2. Topological sort per scenario → column assignment
  //    Hidden scenarios get colCount=1 (collapsed node)
  interface ScenarioLayout {
    scenario: Scenario;
    orderedSteps: { stepId: string; laneId: string; col: number }[];
    colCount: number;
    collapsed: boolean;
  }

  const scenarioLayouts: ScenarioLayout[] = [];

  for (const sc of scenarios) {
    // Hidden scenario → collapsed placeholder (1 column)
    if (!sc.visible) {
      scenarioLayouts.push({
        scenario: sc,
        orderedSteps: [{ stepId: `collapsed-${sc.id}`, laneId: sc.diagram.lanes[0]?.id || 'app', col: 0 }],
        colCount: 1,
        collapsed: true,
      });
      continue;
    }
    const adj = new Map<string, string[]>();
    const inDeg = new Map<string, number>();
    for (const step of sc.diagram.steps) {
      adj.set(step.id, []);
      inDeg.set(step.id, 0);
    }
    for (const conn of sc.diagram.connections) {
      adj.get(conn.from)?.push(conn.to);
      inDeg.set(conn.to, (inDeg.get(conn.to) || 0) + 1);
    }

    const queue: string[] = [];
    const colOf = new Map<string, number>();
    for (const [id, deg] of inDeg) {
      if (deg === 0) { queue.push(id); colOf.set(id, 0); }
    }

    let maxCol = 0;
    while (queue.length > 0) {
      const cur = queue.shift()!;
      const curCol = colOf.get(cur) || 0;
      for (const next of (adj.get(cur) || [])) {
        const nextCol = Math.max(colOf.get(next) || 0, curCol + 1);
        colOf.set(next, nextCol);
        maxCol = Math.max(maxCol, nextCol);
        const newDeg = (inDeg.get(next) || 1) - 1;
        inDeg.set(next, newDeg);
        if (newDeg === 0) queue.push(next);
      }
    }

    // Fallback for disconnected steps
    const sorted = [...sc.diagram.steps].sort((a, b) => a.order - b.order);
    let fallbackCol = maxCol + 1;
    for (const step of sorted) {
      if (!colOf.has(step.id)) {
        colOf.set(step.id, fallbackCol++);
        maxCol = Math.max(maxCol, fallbackCol - 1);
      }
    }

    const orderedSteps = sorted.map(step => ({
      stepId: step.id,
      laneId: step.laneId,
      col: colOf.get(step.id) || 0,
    }));

    scenarioLayouts.push({ scenario: sc, orderedSteps, colCount: maxCol + 1, collapsed: false });
  }

  // 3. X-offset for each scenario (stacked horizontally)
  const scenarioXOffset: number[] = [];
  let currentX = LANE_LABEL_WIDTH + STEP_GAP_X;
  for (const layout of scenarioLayouts) {
    scenarioXOffset.push(currentX);
    currentX += layout.colCount * (STEP_WIDTH + STEP_GAP_X) + SCENARIO_GAP_X;
  }

  // 4. Calculate total width considering saved positions and manual override
  let maxStepRight = currentX;
  for (const [nodeId, pos] of Object.entries(positions)) {
    if (nodeId.startsWith('step-')) {
      maxStepRight = Math.max(maxStepRight, pos.x + STEP_WIDTH + LANE_PADDING_X);
    }
  }
  const autoWidth = Math.max(maxStepRight, currentX) + LANE_LABEL_WIDTH;
  const totalWidth = manualTotalWidth !== null ? Math.max(manualTotalWidth, autoWidth) : autoWidth;

  // 5. Lane heights (count max rows per lane)
  const laneRowCount = new Map<string, number>();
  for (const layout of scenarioLayouts) {
    const stackPerLaneCol = new Map<string, number>();
    for (const s of layout.orderedSteps) {
      const key = `${s.laneId}:${s.col}`;
      stackPerLaneCol.set(key, (stackPerLaneCol.get(key) || 0) + 1);
    }
    for (const [key, count] of stackPerLaneCol) {
      const laneId = key.split(':')[0];
      laneRowCount.set(laneId, Math.max(laneRowCount.get(laneId) || 1, count));
    }
  }

  // 6. Build step→laneId lookup for saved-position checks
  const stepLaneLookup = new Map<string, string>();
  for (const layout of scenarioLayouts) {
    if (layout.collapsed) continue;
    for (const s of layout.orderedSteps) {
      stepLaneLookup.set(s.stepId, s.laneId);
    }
  }

  // 6a. Compute base lane heights from layout (respecting manual overrides)
  const laneHeights = new Map<string, number>();
  for (const [laneId] of sortedLanes) {
    const rows = laneRowCount.get(laneId) || 1;
    const autoH = LANE_PADDING_Y * 2 + rows * (STEP_HEIGHT + STEP_GAP_Y);
    laneHeights.set(laneId, Math.max(autoH, manualLaneHeights[laneId] || 0));
  }

  // 6b. Compute initial Y positions
  const laneYPositions = new Map<string, { y: number; height: number }>();
  let laneY = 0;
  for (const [laneId] of sortedLanes) {
    const height = laneHeights.get(laneId)!;
    laneYPositions.set(laneId, { y: laneY, height });
    laneY += height;
  }

  // 6c. Expand lanes to fit dragged nodes (saved positions)
  let anyExpanded = false;
  for (const [nodeId, pos] of Object.entries(positions)) {
    if (!nodeId.startsWith('step-')) continue;
    const stepId = nodeId.replace(/^step-/, '');
    const laneId = stepLaneLookup.get(stepId);
    if (!laneId) continue;
    const info = laneYPositions.get(laneId);
    if (!info) continue;
    const stepBottom = pos.y + STEP_HEIGHT + LANE_PADDING_Y;
    const laneBottom = info.y + info.height;
    if (stepBottom > laneBottom) {
      info.height += stepBottom - laneBottom;
      anyExpanded = true;
    }
  }

  // 6d. Recompute Y positions if any lane expanded
  if (anyExpanded) {
    let newY = 0;
    for (const [laneId] of sortedLanes) {
      const info = laneYPositions.get(laneId)!;
      info.y = newY;
      newY += info.height;
    }
  }

  // 6e. Create lane nodes with final dimensions
  for (const [laneId, lane] of sortedLanes) {
    const info = laneYPositions.get(laneId)!;
    const laneNodeId = `lane-${laneId}`;
    const data: LaneNodeData = {
      label: lane.label,
      color: lane.color,
      laneId,
      order: lane.order,
      height: info.height,
    };
    nodes.push({
      id: laneNodeId,
      type: 'laneNode',
      position: { x: 0, y: info.y },
      data,
      draggable: false,
      selectable: false,
      style: {
        width: totalWidth,
        height: info.height,
        zIndex: 0,
        pointerEvents: 'none',
      },
    });
  }

  // 7. Place step nodes (visible = full steps, hidden = collapsed placeholder)
  const laneColStack = new Map<string, number>();

  for (let si = 0; si < scenarioLayouts.length; si++) {
    const layout = scenarioLayouts[si];
    const xBase = scenarioXOffset[si];

    // Collapsed scenario → single placeholder node
    if (layout.collapsed) {
      const s = layout.orderedSteps[0];
      const laneInfo = laneYPositions.get(s.laneId);
      if (!laneInfo) continue;

      const stepNodeId = `step-${s.stepId}`;
      const pos = positions[stepNodeId] || {
        x: xBase,
        y: laneInfo.y + LANE_PADDING_Y,
      };
      const laneColor = laneMap.get(s.laneId)?.color || '#f5f5f5';
      const data: StepNodeData = {
        label: layout.scenario.title,
        description: `Сценарий «${layout.scenario.title}» свёрнут`,
        stepType: 'collapsed',
        scenarioId: layout.scenario.id,
        scenarioColor: layout.scenario.color,
        laneId: s.laneId,
        laneColor,
      };

      nodes.push({
        id: stepNodeId,
        type: 'stepNode',
        position: pos,
        data,
        style: { zIndex: 10 },
      });
      continue;
    }

    for (const s of layout.orderedSteps) {
      const laneInfo = laneYPositions.get(s.laneId);
      if (!laneInfo) continue;

      const stepNodeId = `step-${s.stepId}`;

      let pos = positions[stepNodeId];
      if (!pos) {
        const stackKey = `${s.laneId}:${si}:${s.col}`;
        const stackIdx = laneColStack.get(stackKey) || 0;
        laneColStack.set(stackKey, stackIdx + 1);

        pos = {
          x: xBase + s.col * (STEP_WIDTH + STEP_GAP_X),
          y: laneInfo.y + LANE_PADDING_Y + stackIdx * (STEP_HEIGHT + STEP_GAP_Y),
        };
      }

      const laneColor = laneMap.get(s.laneId)?.color || '#f5f5f5';
      const step = layout.scenario.diagram.steps.find(st => st.id === s.stepId);
      const data: StepNodeData = {
        label: step?.label || s.stepId,
        description: step?.description,
        stepType: step?.type || 'action',
        scenarioId: layout.scenario.id,
        scenarioColor: layout.scenario.color,
        laneId: s.laneId,
        laneColor,
      };

      nodes.push({
        id: stepNodeId,
        type: 'stepNode',
        position: pos,
        data,
        style: { zIndex: 10 },
      });
    }
  }

  return nodes;
}

/**
 * Edge handle routing:
 * - Same lane → horizontal (right-out → left-in)
 * - Different lanes → vertical (bottom-out → top-in or top-in → bottom-out)
 */
function getHandles(
  fromLaneId: string,
  toLaneId: string,
  laneOrder: Map<string, number>,
): { sourceHandle: string; targetHandle: string } {
  if (fromLaneId === toLaneId) {
    return { sourceHandle: 'right-out', targetHandle: 'left-in' };
  }
  const fromOrder = laneOrder.get(fromLaneId) ?? 0;
  const toOrder = laneOrder.get(toLaneId) ?? 0;
  if (fromOrder < toOrder) {
    return { sourceHandle: 'bottom-out', targetHandle: 'top-in' };
  }
  return { sourceHandle: 'top-in', targetHandle: 'bottom-out' };
}

function buildEdges(scenarios: Scenario[], waypoints: Record<string, XYPosition> = {}): Edge[] {
  const edges: Edge[] = [];

  // Build lane order from ALL scenarios
  const laneOrder = new Map<string, number>();
  for (const sc of scenarios) {
    for (const lane of sc.diagram.lanes) {
      if (!laneOrder.has(lane.id)) {
        laneOrder.set(lane.id, lane.order ?? laneOrder.size);
      }
    }
  }

  // Build step→laneId lookup (visible only)
  const stepLaneMap = new Map<string, string>();
  for (const sc of scenarios) {
    if (!sc.visible) {
      // Collapsed node lane
      const collapsedLane = sc.diagram.lanes[0]?.id || 'app';
      stepLaneMap.set(`collapsed-${sc.id}`, collapsedLane);
      continue;
    }
    for (const step of sc.diagram.steps) {
      stepLaneMap.set(step.id, step.laneId);
    }
  }

  // Edges for visible scenarios
  for (const sc of scenarios) {
    if (!sc.visible) continue;
    for (const conn of sc.diagram.connections) {
      const edgeId = `edge-${sc.id}-${conn.from}-${conn.to}`;
      const fromLane = stepLaneMap.get(conn.from) || '';
      const toLane = stepLaneMap.get(conn.to) || '';
      const { sourceHandle, targetHandle } = getHandles(fromLane, toLane, laneOrder);

      edges.push({
        id: edgeId,
        source: `step-${conn.from}`,
        target: `step-${conn.to}`,
        sourceHandle,
        targetHandle,
        type: 'flowEdge',
        label: conn.label,
        data: {
          connectionType: conn.type || 'normal',
          scenarioColor: sc.color,
          scenarioId: sc.id,
          waypoint: waypoints[edgeId] || null,
        },
        style: {
          stroke: conn.type === 'error' ? '#e53935' :
                  conn.type === 'conditional' ? '#f9a825' : sc.color,
          strokeWidth: 1.5,
        },
        animated: conn.type === 'conditional',
      });
    }
  }

  // Cross-scenario chaining: connect adjacent scenarios
  // For visible → visible: end → start
  // For visible → collapsed: end → collapsed node
  // For collapsed → visible: collapsed node → start
  // For collapsed → collapsed: collapsed → collapsed
  for (let i = 0; i < scenarios.length - 1; i++) {
    const curr = scenarios[i];
    const next = scenarios[i + 1];

    // Determine source node ID
    let sourceNodeId: string;
    let fromLane: string;
    if (curr.visible) {
      const endSteps = curr.diagram.steps.filter(s => s.type === 'end');
      if (endSteps.length === 0) continue;
      sourceNodeId = `step-${endSteps[0].id}`;
      fromLane = stepLaneMap.get(endSteps[0].id) || '';
    } else {
      sourceNodeId = `step-collapsed-${curr.id}`;
      fromLane = stepLaneMap.get(`collapsed-${curr.id}`) || '';
    }

    // Determine target node ID
    let targetNodeId: string;
    let toLane: string;
    if (next.visible) {
      const startSteps = next.diagram.steps.filter(s => s.type === 'start');
      if (startSteps.length === 0) continue;
      targetNodeId = `step-${startSteps[0].id}`;
      toLane = stepLaneMap.get(startSteps[0].id) || '';
    } else {
      targetNodeId = `step-collapsed-${next.id}`;
      toLane = stepLaneMap.get(`collapsed-${next.id}`) || '';
    }

    const { sourceHandle, targetHandle } = getHandles(fromLane, toLane, laneOrder);

    edges.push({
      id: `chain-${curr.id}-${next.id}`,
      source: sourceNodeId,
      target: targetNodeId,
      sourceHandle,
      targetHandle,
      type: 'flowEdge',
      label: next.visible ? '' : `→ ${next.title}`,
      data: {
        connectionType: 'normal',
        scenarioColor: '#888',
        scenarioId: `chain-${curr.id}-${next.id}`,
        waypoint: waypoints[`chain-${curr.id}-${next.id}`] || null,
      },
      style: {
        stroke: '#888',
        strokeWidth: 2,
        strokeDasharray: '8 4',
      },
      animated: !curr.visible || !next.visible,
    });
  }

  return edges;
}

/* ─── Zustand store ─── */
export const useDiagramStore = create<DiagramStore>((set, get) => ({
  scenarios: [],
  nodes: [],
  edges: [],
  collisions: [],
  selectedNodeId: null,
  selectedScenarioId: null,
  nodePositions: loadPositions(),
  edgeWaypoints: loadWaypoints(),
  partners: loadPartners(),
  manualLaneHeights: loadLaneHeights(),
  manualTotalWidth: loadTotalWidth(),
  loading: false,

  addScenario: (diagram: SwimlaneDiagram) => {
    const { scenarios, nodePositions } = get();
    const existing = scenarios.findIndex(s => s.id === diagram.id);
    const colorIndex = existing >= 0 ? scenarios.indexOf(scenarios[existing]) : scenarios.length;
    const color = diagram.color || SCENARIO_COLORS[colorIndex % SCENARIO_COLORS.length];

    const scenario: Scenario = {
      id: diagram.id,
      title: diagram.title,
      description: diagram.description,
      color,
      visible: true,
      diagram,
    };

    let updated: Scenario[];
    if (existing >= 0) {
      updated = [...scenarios];
      updated[existing] = scenario;
    } else {
      updated = [...scenarios, scenario];
    }

    const { edgeWaypoints, partners } = get();
    const nodes = buildNodes(updated, nodePositions, partners);
    const edges = buildEdges(updated, edgeWaypoints);
    set({ scenarios: updated, nodes, edges, collisions: [] });
  },

  removeScenario: (id: string) => {
    const { scenarios, nodePositions, edgeWaypoints, partners } = get();
    const updated = scenarios.filter(s => s.id !== id);
    const nodes = buildNodes(updated, nodePositions, partners);
    const edges = buildEdges(updated, edgeWaypoints);
    set({ scenarios: updated, nodes, edges, collisions: [] });
  },

  deleteScenario: async (id: string) => {
    try {
      const { default: apiService } = await import('../../../helpers/apiService');
      await apiService.diagramDeleteScenario(id);
    } catch (e) {
      console.error('Failed to delete scenario from DB:', e);
    }
    // Remove from local state regardless
    get().removeScenario(id);
  },

  createScenario: async (diagram: SwimlaneDiagram) => {
    try {
      const { default: apiService } = await import('../../../helpers/apiService');
      await apiService.diagramSaveScenario(diagram);
    } catch (e) {
      console.error('Failed to save scenario to DB:', e);
    }
    get().addScenario(diagram);
  },

  toggleScenario: (id: string) => {
    const { scenarios, nodePositions, edgeWaypoints, partners } = get();
    const updated = scenarios.map(s => s.id === id ? { ...s, visible: !s.visible } : s);
    const nodes = buildNodes(updated, nodePositions, partners);
    const edges = buildEdges(updated, edgeWaypoints);
    set({ scenarios: updated, nodes, edges });
  },

  reorderScenarios: (fromIndex: number, toIndex: number) => {
    const { scenarios, nodePositions, edgeWaypoints, partners } = get();
    const updated = [...scenarios];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    const nodes = buildNodes(updated, nodePositions, partners);
    const edges = buildEdges(updated, edgeWaypoints);
    set({ scenarios: updated, nodes, edges });
  },

  onNodesChange: (changes) => {
    set({ nodes: applyNodeChanges(changes, get().nodes) });
  },

  onEdgesChange: (changes) => {
    // Sync edge removals back to scenario data
    for (const change of changes) {
      if (change.type === 'remove') {
        get().removeConnection(change.id);
        return; // removeConnection already updates edges via buildEdges
      }
    }
    set({ edges: applyEdgeChanges(changes, get().edges) });
  },

  updateNodePosition: (id: string, position: XYPosition) => {
    const { nodePositions, scenarios, edgeWaypoints, partners } = get();
    const updated = { ...nodePositions, [id]: position };
    set({ nodePositions: updated });
    savePositions(updated);

    // Rebuild to recalculate lane widths (dynamic expansion)
    const nodes = buildNodes(scenarios, updated, partners);
    const edges = buildEdges(scenarios, edgeWaypoints);
    set({ nodes, edges });
  },

  updateEdgeWaypoint: (edgeId: string, pos: XYPosition) => {
    const { edgeWaypoints, scenarios } = get();
    const updated = { ...edgeWaypoints, [edgeId]: pos };
    set({ edgeWaypoints: updated });
    saveWaypoints(updated);
    const edges = buildEdges(scenarios, updated);
    set({ edges });
  },

  clearEdgeWaypoint: (edgeId: string) => {
    const { edgeWaypoints, scenarios } = get();
    const updated = { ...edgeWaypoints };
    delete updated[edgeId];
    set({ edgeWaypoints: updated });
    saveWaypoints(updated);
    const edges = buildEdges(scenarios, updated);
    set({ edges });
  },

  selectNode: (id: string | null) => {
    set({ selectedNodeId: id });
  },

  rebuildDiagram: () => {
    const { scenarios, nodePositions, edgeWaypoints, partners, manualLaneHeights, manualTotalWidth } = get();
    const nodes = buildNodes(scenarios, nodePositions, partners, manualLaneHeights, manualTotalWidth);
    const edges = buildEdges(scenarios, edgeWaypoints);
    set({ nodes, edges });
  },

  addPartner: (partner: Partner) => {
    const { partners } = get();
    if (partners.find(p => p.id === partner.id)) return;
    const updated = [...partners, partner];
    savePartners(updated);
    set({ partners: updated });
    get().rebuildDiagram();
  },

  removePartner: (id: string) => {
    const { partners } = get();
    const updated = partners.filter(p => p.id !== id);
    savePartners(updated);
    set({ partners: updated });
    get().rebuildDiagram();
  },

  addConnection: (connection: Connection, scenarioColor: string, _scenarioId: string) => {
    const { scenarios, edges } = get();

    const sourceStepId = connection.source?.replace(/^step-/, '') ?? '';
    const targetStepId = connection.target?.replace(/^step-/, '') ?? '';
    if (!sourceStepId || !targetStepId || sourceStepId === targetStepId) return;

    // Find scenario
    const scenario = scenarios.find(sc =>
      sc.diagram.steps.some(s => s.id === sourceStepId),
    );
    if (!scenario) return;
    if (!scenario.diagram.steps.some(s => s.id === targetStepId)) return;
    if (scenario.diagram.connections.some(c => c.from === sourceStepId && c.to === targetStepId)) return;

    // 1. Add edge directly to React Flow edges state (immediate visual)
    const edgeId = `edge-${scenario.id}-${sourceStepId}-${targetStepId}`;
    const newEdge: Edge = {
      id: edgeId,
      source: connection.source!,
      target: connection.target!,
      sourceHandle: connection.sourceHandle ?? 'right-out',
      targetHandle: connection.targetHandle ?? 'left-in',
      type: 'flowEdge',
      style: { stroke: scenarioColor, strokeWidth: 1.5 },
      data: {
        connectionType: 'normal',
        scenarioColor,
        scenarioId: scenario.id,
        waypoint: null,
      },
    };
    set({ edges: [...edges, newEdge] });

    // 2. Persist connection to scenario data
    const newConn: SwimlaneConnection = { from: sourceStepId, to: targetStepId, type: 'normal' };
    const updatedDiagram: SwimlaneDiagram = {
      ...scenario.diagram,
      connections: [...scenario.diagram.connections, newConn],
    };
    const updated = scenarios.map(s =>
      s.id === scenario.id ? { ...s, diagram: updatedDiagram } : s,
    );
    set({ scenarios: updated });

    import('../../../helpers/apiService').then(({ default: api }) => {
      api.diagramSaveScenario(updatedDiagram).catch(console.error);
    });
  },

  removeConnection: (edgeId: string) => {
    const { scenarios, edges, edgeWaypoints } = get();
    const edge = edges.find(e => e.id === edgeId);
    if (!edge) return;

    const edata = edge.data as Record<string, unknown>;
    const scenarioId = edata?.scenarioId as string;
    const sourceStepId = edge.source.replace(/^step-/, '');
    const targetStepId = edge.target.replace(/^step-/, '');

    // Clear waypoint
    const newWaypoints = { ...edgeWaypoints };
    delete newWaypoints[edgeId];
    saveWaypoints(newWaypoints);

    const scenario = scenarios.find(sc => sc.id === scenarioId);
    if (!scenario) {
      set({ edges: edges.filter(e => e.id !== edgeId), edgeWaypoints: newWaypoints });
      return;
    }

    const updatedDiagram: SwimlaneDiagram = {
      ...scenario.diagram,
      connections: scenario.diagram.connections.filter(
        c => !(c.from === sourceStepId && c.to === targetStepId),
      ),
    };
    const updatedScenarios = scenarios.map(s =>
      s.id === scenarioId ? { ...s, diagram: updatedDiagram } : s,
    );
    const newEdges = buildEdges(updatedScenarios, newWaypoints);
    set({ scenarios: updatedScenarios, edges: newEdges, edgeWaypoints: newWaypoints });

    import('../../../helpers/apiService').then(({ default: api }) => {
      api.diagramSaveScenario(updatedDiagram).catch(console.error);
    });
  },

  reconnectConnection: (oldEdgeId: string, newConnection: Connection) => {
    const { scenarios, edges, edgeWaypoints } = get();
    const oldEdge = edges.find(e => e.id === oldEdgeId);
    if (!oldEdge) return;

    const edata = oldEdge.data as Record<string, unknown>;
    const scenarioId = edata?.scenarioId as string;
    const oldFrom = oldEdge.source.replace(/^step-/, '');
    const oldTo = oldEdge.target.replace(/^step-/, '');
    const newFrom = (newConnection.source ?? '').replace(/^step-/, '');
    const newTo = (newConnection.target ?? '').replace(/^step-/, '');

    if (!newFrom || !newTo || newFrom === newTo) return;

    const scenario = scenarios.find(sc => sc.id === scenarioId);
    if (!scenario) return;

    // Replace old connection with new one
    const existingConn = scenario.diagram.connections.find(c => c.from === oldFrom && c.to === oldTo);
    const updatedDiagram: SwimlaneDiagram = {
      ...scenario.diagram,
      connections: [
        ...scenario.diagram.connections.filter(c => !(c.from === oldFrom && c.to === oldTo)),
        { from: newFrom, to: newTo, type: existingConn?.type || 'normal', label: existingConn?.label },
      ],
    };

    // Clear old waypoint
    const newWaypoints = { ...edgeWaypoints };
    delete newWaypoints[oldEdgeId];
    saveWaypoints(newWaypoints);

    const updatedScenarios = scenarios.map(s =>
      s.id === scenarioId ? { ...s, diagram: updatedDiagram } : s,
    );
    const newEdges = buildEdges(updatedScenarios, newWaypoints);
    set({ scenarios: updatedScenarios, edges: newEdges, edgeWaypoints: newWaypoints });

    import('../../../helpers/apiService').then(({ default: api }) => {
      api.diagramSaveScenario(updatedDiagram).catch(console.error);
    });
  },

  setLaneHeight: (laneId: string, height: number) => {
    const { scenarios, nodePositions, partners, edgeWaypoints, manualLaneHeights, manualTotalWidth } = get();
    const updated = { ...manualLaneHeights, [laneId]: Math.round(height) };
    saveLaneHeights(updated);
    const nodes = buildNodes(scenarios, nodePositions, partners, updated, manualTotalWidth);
    const edges = buildEdges(scenarios, edgeWaypoints);
    set({ manualLaneHeights: updated, nodes, edges });
  },

  setTotalWidth: (width: number | null) => {
    const { scenarios, nodePositions, partners, edgeWaypoints, manualLaneHeights } = get();
    saveTotalWidth(width);
    const nodes = buildNodes(scenarios, nodePositions, partners, manualLaneHeights, width);
    const edges = buildEdges(scenarios, edgeWaypoints);
    set({ manualTotalWidth: width, nodes, edges });
  },

  addStep: (scenarioId: string, step: import('../types').SwimlaneStep) => {
    const { scenarios, nodePositions, edgeWaypoints, partners } = get();
    const scenario = scenarios.find(sc => sc.id === scenarioId);
    if (!scenario) return;

    const updatedDiagram: SwimlaneDiagram = {
      ...scenario.diagram,
      steps: [...scenario.diagram.steps, step],
    };
    const updatedScenarios = scenarios.map(s =>
      s.id === scenarioId ? { ...s, diagram: updatedDiagram } : s,
    );
    const nodes = buildNodes(updatedScenarios, nodePositions, partners);
    const edges = buildEdges(updatedScenarios, edgeWaypoints);
    set({ scenarios: updatedScenarios, nodes, edges });

    import('../../../helpers/apiService').then(({ default: api }) => {
      api.diagramSaveScenario(updatedDiagram).catch(console.error);
    });
  },

  validate: () => {
    const { scenarios } = get();
    const errors = detectCollisions(scenarios);
    set({ collisions: errors });
    return errors;
  },

  loadFromApi: async () => {
    set({ loading: true });
    try {
      const { default: apiService } = await import('../../../helpers/apiService');
      const diagrams: SwimlaneDiagram[] = await apiService.diagramListScenarios();

      // Добавляем SC-000 (единый путь пользователя), если его нет в данных API
      const allDiagrams: SwimlaneDiagram[] = [sc000UserJourney, ...(diagrams || []).filter(d => d.id !== sc000UserJourney.id)];

      const { nodePositions, edgeWaypoints, partners } = get();
      const scenarios: Scenario[] = allDiagrams.map((d, i) => ({
        id: d.id,
        title: d.title,
        description: d.description,
        color: d.color || SCENARIO_COLORS[i % SCENARIO_COLORS.length],
        visible: true,
        diagram: d,
      }));

      const { manualLaneHeights, manualTotalWidth } = get();
      const nodes = buildNodes(scenarios, nodePositions, partners, manualLaneHeights, manualTotalWidth);
      const edges = buildEdges(scenarios, edgeWaypoints);
      set({ scenarios, nodes, edges, collisions: [], loading: false });

      // Вывод бизнес-сценариев в консоль

      const BUSINESS_SCENARIO_IDS = ['sc-001', 'sc-003', 'sc-006', 'sc-008', 'sc-007', 'sc-004', 'sc-005', 'sc-012'];
      const BUSINESS_SCENARIO_NAMES: Record<string, string> = {
        'sc-001': '1. Регистрация Пользователя',
        'sc-003': '2. Идентификация Пользователя (KYC)',
        'sc-006': '3. Создание счёта',
        'sc-008': '4. Пополнение счёта',
        'sc-007': '5. Выпуск карты',
        'sc-004': '6. Авторизация зарегистрированного Пользователя',
        'sc-005': '7. Включение 2FA',
        'sc-012': '8. Проверка транзакции AML',
      };
      console.group('%c📋 Бизнес-сценарии платформы', 'color: #2196f3; font-weight: bold; font-size: 14px');
      for (const id of BUSINESS_SCENARIO_IDS) {
        const sc = scenarios.find(s => s.id === id);
        if (!sc) { console.warn(`  ⚠️  ${BUSINESS_SCENARIO_NAMES[id]} — НЕ НАЙДЕН (id: ${id})`); continue; }
        console.group(`%c${BUSINESS_SCENARIO_NAMES[id]}`, 'color: ' + sc.color + '; font-weight: bold');
        console.log('  ID:      ', sc.id);
        console.log('  Зоны:   ', sc.diagram.lanes.map(l => l.label).join(', '));
        console.log('  Шагов:  ', sc.diagram.steps.length);
        console.log('  Связей: ', sc.diagram.connections.length);
        console.log('  Шаги:   ', sc.diagram.steps.map(s => `[${s.type || 'action'}] ${s.label}`));
        console.log('  Данные: ', sc.diagram);
        console.groupEnd();
      }
      console.groupEnd();
    } catch {
      // API недоступен — загружаем хотя бы SC-000
      const { nodePositions, edgeWaypoints, partners, manualLaneHeights, manualTotalWidth } = get();
      const fallback: Scenario[] = [{
        id: sc000UserJourney.id,
        title: sc000UserJourney.title,
        description: sc000UserJourney.description,
        color: sc000UserJourney.color || SCENARIO_COLORS[0],
        visible: true,
        diagram: sc000UserJourney,
      }];
      const nodes = buildNodes(fallback, nodePositions, partners, manualLaneHeights, manualTotalWidth);
      const edges = buildEdges(fallback, edgeWaypoints);
      set({ scenarios: fallback, nodes, edges, loading: false });
    }
  },

  saveToApi: async () => {
    try {
      const { default: apiService } = await import('../../../helpers/apiService');
      const { scenarios } = get();
      for (const sc of scenarios) {
        await apiService.diagramSaveScenario(sc.diagram);
      }
    } catch (e) {
      console.error('Failed to save diagrams:', e);
    }
  },
}));
